<script lang="ts" setup>
/**
 * @link https://github.com/alphagov/accessible-autocomplete
 */
import {
  computed,
  InputHTMLAttributes,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  useAttrs,
} from 'vue'
// @ts-ignore
import accessibleAutocomplete from 'accessible-autocomplete'
// @ts-ignore
import * as diacritic from 'diacritic'
import { safeDomId } from '../../../utils/formValidations'
import { IDSKSelectOptions } from '../IDSKSelect.vue'

export interface IDSKAutocompleteProps extends InputHTMLAttributes {
  options: ReadonlyArray<IDSKSelectOptions>
  optionObjectValue?: string
  optionObjectLabel?: string
  label?: string
  hint?: string
  hintId?: string
  /** Error message text, also serves as a flag whether the input is valid. */
  errorMessage?: string
  errorMessageId?: string
  readOnly?: boolean
  modelValue: IDSKSelectOptions | string
  id: string
  placeholder?: string
  updateOnBlur?: boolean
  dataTestId?: string
}

const props = withDefaults(defineProps<IDSKAutocompleteProps>(), {
  label: undefined,
  optionObjectValue: 'id',
  optionObjectLabel: 'nazov',
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  type: 'text',
  placeholder: 'Zvoľte si možnosť',
  dataTestId: '',
})

const safeId = safeDomId(props.id)

const attrs = useAttrs()

const emits = defineEmits(['update:modelValue'])

const value = computed(() => {
  return typeof props.modelValue === 'object'
    ? props.modelValue?.[props.optionObjectLabel]?.toString()
    : props.modelValue
})

const selectOptions = computed(() => props.options)

const findSelectOption = (value) =>
  selectOptions.value.find(
    (option) => option[props.optionObjectLabel]?.toString() === value
  )

const handleConfirm = (value: string) => {
  emits('update:modelValue', findSelectOption(value))
}

function handleBlurInput(e: any) {
  if (!e.target.value) {
    emits('update:modelValue', undefined)
  } else if (props.updateOnBlur) {
    const option = findSelectOption(e.target.value)
    emits('update:modelValue', option ? option : e.target.value)
  }
}

onMounted(() => {
  const selectElement = document.querySelector(`#${safeId}-autocomplete-wrapper`)
  // init only select
  if (selectElement && selectElement.options) {
    accessibleAutocomplete.enhanceSelectElement({
      source: (query: string, syncResults: Function) => {
        var matches = selectOptions.value?.filter((selectOption) => {
          const option =
            selectOption?.[props.optionObjectLabel]?.toString() ??
            selectOption?.toString()
          const cleanValue = diacritic.clean(option.toLowerCase())
          const cleanQuery = diacritic.clean(query.toLowerCase())
          return cleanValue.indexOf(cleanQuery) !== -1
        })
        syncResults(
          matches.map(
            (option) =>
              option?.[props.optionObjectLabel]?.toString() ?? option?.toString()
          )
        )
      },
      defaultValue: value.value,
      autoselect: true,
      showAllValues: true,
      selectElement,
      // placeholder: props.placeholder,
      displayMenu: 'overlay',
      confirmOnBlur: false,
      tNoResults: () => 'Nenašli sa žiadne výsledky',
      onConfirm: handleConfirm,
    })

    /**
     * Ak používateľ zmaže hodnotu v autocomplete, trigerneme zmenu aj pre model-value.
     * 'accessible-autocomplete' vyrobí zo select elementu svoj vlastný input,
     * ale ten nie je nikde dostupný, preto si ho potrebujeme nájsť
     */
    const input = document.querySelector(`#${safeId}-autocomplete-wrapper`)
    input?.setAttribute(
      'data-test-id',
      safeId
        ? 'input-autocomplete' + `${safeId}-autocomplete-wrapper`
        : 'input-autocomplete' + props.dataTestId
    )
    input?.addEventListener('blur', handleBlurInput)
  }
})

onBeforeUnmount(() => {
  const input = document.querySelector(`#${safeId}-autocomplete-wrapper`)
  input?.removeEventListener('blur', handleBlurInput)
})
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        ['govuk-form-group--error']: props.errorMessage,
      },
    ]"
  >
    <IDSKFieldCaption
      :label="props.label"
      :for="`${safeId}-autocomplete-wrapper`"
      :hint="props.hint ?? props.placeholder"
      :hint-id="props.hintId"
      :error-message="props.errorMessage"
      :error-message-id="props.errorMessageId"
    />
    <select
      :id="`${safeId}-autocomplete-wrapper`"
      class="govuk-body"
      :data-test-id="
        safeId
          ? 'select-autocomplete' + `${safeId}-autocomplete-wrapper`
          : 'select-autocomplete' + props.dataTestId
      "
    >
      <option label="Zvoľte si možnosť" />
      <template v-for="option in selectOptions" :key="option[props.optionObjectLabel]">
        <option :value="option[props.optionObjectValue]?.toString()">
          {{ option[props.optionObjectLabel]?.toString() }}
        </option>
      </template>
    </select>
  </div>
</template>

<style lang="scss" >
.autocomplete__hint {
  padding-left: 10px;
}

.autocomplete__input {
  transition: all 0.195s ease-in;
  padding-left: 10px;

  &:focus {
    margin-right: 3px;
    z-index: 3;
    border: 2px solid govuk-colour('black');
    box-shadow: inset 0 0 0 3px;
  }

  &:hover {
    margin-right: 0px;
    box-shadow: inset 0 0 0 3px;
  }
}

.autocomplete__menu--overlay {
  border-radius: 0 0 2px 2px;
}
</style>
